import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { apiClient } from '../../services/api/client';
import AuthBrandPanel from '../../components/auth/AuthBrandPanel';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
    const navigate = useNavigate();
  const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [validationResult, setValidationResult] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
      setError('No invitation token provided.');
            setLoading(false);
            return;
        }

   validateToken();
  }, [token]);

    const validateToken = async () => {
    try {
         const response = await apiClient.get('/users/invitations/validate', { token });
     if (response.success && response.data) {
         setValidationResult(response.data);
       if (!response.data.isValid) {
     setError(response.data.error || 'Invalid invitation.');
     }
            } else {
     setError('Failed to validate invitation.');
            }
        } catch (err) {
            setError(err.message || 'Failed to validate invitation.');
        } finally {
      setLoading(false);
        }
 };

    const validatePassword = () => {
        if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters.');
       return false;
        }
        if (!/[A-Z]/.test(password)) {
  setPasswordError('Password must contain at least one uppercase letter.');
       return false;
        }
        if (!/[a-z]/.test(password)) {
     setPasswordError('Password must contain at least one lowercase letter.');
       return false;
     }
        if (!/[0-9]/.test(password)) {
   setPasswordError('Password must contain at least one number.');
      return false;
        }
        if (password !== confirmPassword) {
    setPasswordError('Passwords do not match.');
          return false;
        }
setPasswordError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePassword()) {
      return;
    }

        setSubmitting(true);
        setError(null);

        try {
     const response = await apiClient.post('/users/invitations/accept', {
    token,
       password
    });

            if (response.success) {
            setSuccess(true);
            } else {
  setError(response.message || 'Failed to accept invitation.');
   }
        } catch (err) {
    setError(err.message || 'Failed to accept invitation.');
        } finally {
            setSubmitting(false);
    }
    };

    const getPasswordStrength = () => {
        let strength = 0;
        if (password.length >= 8) strength++;
     if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  return strength;
    };

    const passwordStrength = getPasswordStrength();
    const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-[#00C9A7]'];

    // Loading state
    if (loading) {
    return (
         <div className="min-h-screen flex bg-[#f8f9fc]">
                <AuthBrandPanel />
   <div className="flex-1 flex items-center justify-center p-6">
       <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            <div className="w-5 h-5 border-2 border-[#FF5A5F]/30 border-t-[#FF5A5F] rounded-full animate-spin mx-auto mb-4" />
             <p className="text-gray-500">Validating invitation...</p>
          </div>
      </div>
</div>
        );
    }

    // Success state
    if (success) {
        return (
            <>
         <Helmet>
 <title>Account Created - Rocket Tronc</title>
        </Helmet>
         <div className="min-h-screen flex bg-[#f8f9fc]">
            <AuthBrandPanel />
           <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
      <div className="lg:hidden text-center mb-8">
           <img src="/assets/images/RocketOS_Brandmark_RGB.png" alt="Rocket" className="h-12 w-auto mx-auto mb-2" />
         </div>
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 bg-[#00C9A7]/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Icon name="CheckCircle" size={32} className="text-[#00C9A7]" />
  </div>
        <h1 className="text-2xl font-heading font-bold text-[#1a2b4b] mb-4">Welcome to Rocket Tronc!</h1>
  <p className="text-gray-500 mb-6">
         Your account has been created successfully. You can now log in with your email and password.
       </p>
               <Link
           to="/login"
className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-[#FF5A5F] hover:bg-[#e54e53] text-white font-semibold rounded-xl shadow-lg shadow-[#FF5A5F]/25 transition-all"
     >
    <Icon name="LogIn" size={18} />
Go to Login
      </Link>
         </div>
      <p className="text-center text-sm text-gray-400 mt-8 lg:hidden">
  &copy; {new Date().getFullYear()} Rocket. All rights reserved.
         </p>
          </div>
        </div>
    </div>
       </>
        );
    }

    // Error state (invalid/expired token)
    if (error && !validationResult?.isValid) {
        return (
          <>
  <Helmet>
        <title>Invalid Invitation - Rocket Tronc</title>
  </Helmet>
          <div className="min-h-screen flex bg-[#f8f9fc]">
     <AuthBrandPanel />
    <div className="flex-1 flex items-center justify-center p-6">
              <div className="w-full max-w-md">
     <div className="lg:hidden text-center mb-8">
         <img src="/assets/images/RocketOS_Brandmark_RGB.png" alt="Rocket" className="h-12 w-auto mx-auto mb-2" />
              </div>
           <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
     <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
       <Icon name="AlertTriangle" size={32} className="text-[#FF5A5F]" />
 </div>
       <h1 className="text-2xl font-heading font-bold text-[#1a2b4b] mb-4">Invalid Invitation</h1>
        <p className="text-gray-500 mb-2">
             {error || 'This invitation link is invalid or has expired.'}
               </p>
     <p className="text-sm text-gray-400 mb-6">
      Please contact your administrator to request a new invitation.
         </p>
        <Link
   to="/login"
           className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-200 text-[#1a2b4b] font-semibold rounded-xl hover:bg-gray-50 transition-all"
         >
  <Icon name="ArrowLeft" size={18} />
      Back to Login
          </Link>
         </div>
       <p className="text-center text-sm text-gray-400 mt-8 lg:hidden">
             &copy; {new Date().getFullYear()} Rocket. All rights reserved.
    </p>
 </div>
        </div>
             </div>
            </>
        );
    }

    // Accept invitation form
    return (
        <>
  <Helmet>
    <title>Accept Invitation - Rocket Tronc</title>
  </Helmet>
            <div className="min-h-screen flex bg-[#f8f9fc]">
         <AuthBrandPanel />
  <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-md">
        {/* Mobile Logo */}
   <div className="lg:hidden text-center mb-8">
     <img src="/assets/images/RocketOS_Brandmark_RGB.png" alt="Rocket" className="h-12 w-auto mx-auto mb-2" />
<p className="text-sm text-gray-500">TRONC Management</p>
        </div>

       {/* Header */}
    <div className="text-center mb-8">
       <h2 className="text-3xl font-heading font-bold text-[#1a2b4b]">
               Welcome, {validationResult?.firstName}!
   </h2>
    <p className="text-gray-500 mt-2">
        You've been invited to join{' '}
    {validationResult?.tenantName ? (
    <strong className="text-[#1a2b4b]">{validationResult.tenantName}</strong>
         ) : (
              'Rocket Tronc'
      )}
       </p>
              </div>

   {/* Form Card */}
       <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
   {/* Errors */}
                {(passwordError || error) && (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 mb-6">
        <Icon name="AlertCircle" size={20} className="shrink-0" />
     <span className="text-sm font-medium">{passwordError || error}</span>
       </div>
         )}

     <form onSubmit={handleSubmit} className="space-y-5">
                   {/* Email (read-only) */}
                 <div>
         <label className="block text-sm font-medium text-[#1a2b4b] mb-2">Email</label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
   <Icon name="Mail" size={18} className="text-gray-400" />
        </div>
     <input
     type="email"
     value={validationResult?.email || ''}
    disabled
          className="w-full pl-11 pr-4 py-3 text-gray-500 bg-gray-100 border border-gray-200 rounded-xl cursor-not-allowed"
      />
    </div>
         </div>

     {/* Password */}
              <div>
         <label htmlFor="password" className="block text-sm font-medium text-[#1a2b4b] mb-2">Create Password</label>
      <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
   <Icon name="Lock" size={18} className="text-gray-400" />
            </div>
        <input
 id="password"
         type={showPassword ? 'text' : 'password'}
      value={password}
                onChange={(e) => setPassword(e.target.value)}
    className="w-full pl-11 pr-12 py-3 text-[#1a2b4b] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5A5F]/20 focus:border-[#FF5A5F] transition-all"
         placeholder="Enter your password"
        required
       minLength={8}
     autoFocus
   />
     <button
   type="button"
               onClick={() => setShowPassword(!showPassword)}
    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-[#1a2b4b] transition-colors"
  >
           <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
           </div>

             {/* Password Strength Indicator */}
                 {password && (
<div className="mt-2">
         <div className="flex gap-1 mb-1">
  {[0, 1, 2, 3, 4].map((i) => (
   <div
          key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
    i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'
        }`}
       />
              ))}
             </div>
        <p className="text-xs text-gray-500">
          Strength: {strengthLabels[passwordStrength - 1] || 'Very Weak'}
               </p>
         </div>
          )}

            {/* Requirements */}
         <div className="mt-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
     <p className="text-xs font-semibold text-[#1a2b4b] mb-2">Password must have:</p>
       <div className="grid grid-cols-2 gap-1">
             <div className={`text-xs flex items-center gap-1.5 ${password.length >= 8 ? 'text-[#00C9A7]' : 'text-gray-400'}`}>
            <Icon name={password.length >= 8 ? 'Check' : 'Circle'} size={12} /> 8+ characters
            </div>
          <div className={`text-xs flex items-center gap-1.5 ${/[A-Z]/.test(password) ? 'text-[#00C9A7]' : 'text-gray-400'}`}>
        <Icon name={/[A-Z]/.test(password) ? 'Check' : 'Circle'} size={12} /> Uppercase
         </div>
 <div className={`text-xs flex items-center gap-1.5 ${/[a-z]/.test(password) ? 'text-[#00C9A7]' : 'text-gray-400'}`}>
                <Icon name={/[a-z]/.test(password) ? 'Check' : 'Circle'} size={12} /> Lowercase
        </div>
     <div className={`text-xs flex items-center gap-1.5 ${/[0-9]/.test(password) ? 'text-[#00C9A7]' : 'text-gray-400'}`}>
       <Icon name={/[0-9]/.test(password) ? 'Check' : 'Circle'} size={12} /> Number
         </div>
         </div>
    </div>
       </div>

                 {/* Confirm Password */}
   <div>
    <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#1a2b4b] mb-2">Confirm Password</label>
      <div className="relative">
    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <Icon name="Lock" size={18} className="text-gray-400" />
        </div>
 <input
       id="confirmPassword"
          type={showPassword ? 'text' : 'password'}
         value={confirmPassword}
      onChange={(e) => setConfirmPassword(e.target.value)}
           className="w-full pl-11 pr-4 py-3 text-[#1a2b4b] bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF5A5F]/20 focus:border-[#FF5A5F] transition-all"
   placeholder="Confirm your password"
        required
        />
          </div>
    {confirmPassword && password !== confirmPassword && (
                 <p className="text-xs text-[#FF5A5F] mt-1 flex items-center gap-1">
         <Icon name="AlertCircle" size={12} /> Passwords do not match
     </p>
    )}
    {confirmPassword && password === confirmPassword && (
    <p className="text-xs text-[#00C9A7] mt-1 flex items-center gap-1">
           <Icon name="Check" size={12} /> Passwords match
         </p>
   )}
     </div>

            {/* Submit Button */}
     <button
      type="submit"
             disabled={submitting || !password || !confirmPassword || password !== confirmPassword || passwordStrength < 3}
className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-[#FF5A5F] hover:bg-[#e54e53] text-white font-semibold rounded-xl shadow-lg shadow-[#FF5A5F]/25 hover:shadow-xl hover:shadow-[#FF5A5F]/30 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
             >
     {submitting ? (
        <>
        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
Creating Account...
             </>
         ) : (
           <>
  <Icon name="UserPlus" size={18} />
       Create Account
              </>
  )}
             </button>
          </form>

    {/* Footer link */}
              <div className="mt-6 text-center">
               <Link to="/login" className="text-sm text-[#FF5A5F] hover:text-[#e54e53] hover:underline flex items-center justify-center gap-2 transition-colors">
 <Icon name="ArrowLeft" size={16} />
                Already have an account? Log in
   </Link>
        </div>
     </div>

   <p className="text-center text-sm text-gray-400 mt-8 lg:hidden">
       &copy; {new Date().getFullYear()} Rocket. All rights reserved.
      </p>
    </div>
         </div>
</div>
   </>
    );
};

export default AcceptInvitation;
